// @flow
import * as React from 'react';
import type { ChildrenArray } from 'react';
import type { MathString } from '../../types.js';
import {
  productColor,
  illustrationColor,
  productColorSunflower,
} from '../../designSystem.js';
import { usePrintMode } from '../../jsx-activity/usePrintMode.js';
import { InternalHeading } from '../../InternalHeading.js';

export function InternalAnchor({
  url,
  title,
  option,
}: {
  url: string,
  title: string,
  option: 'link' | 'pdf',
}) {
  const optionIconDefintions = {
    link: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.0812 0H15.576C15.8101 0 15.9998 0.189732 15.9998 0.423762V4.91852C15.9998 5.15255 15.8101 5.34228 15.576 5.34228H14.446C14.212 5.34228 14.0222 5.15255 14.0222 4.91852V3.37586L8.21699 9.18111C8.13755 9.26061 8.02975 9.30524 7.91737 9.30524C7.80493 9.30524 7.69718 9.26061 7.61768 9.18111L6.81864 8.38201C6.65315 8.21646 6.65315 7.94819 6.81864 7.78269L12.6239 1.97755H11.0812C10.8471 1.97755 10.6574 1.78782 10.6574 1.55379V0.423762C10.6574 0.189732 10.8471 0 11.0812 0ZM13.2672 7.95055H14.3972C14.6312 7.95055 14.821 8.14029 14.8209 8.37437V13.5265C14.8209 14.8904 13.7113 16 12.3473 16H2.47336C1.10958 16 0 14.8905 0 13.5265V3.65259C0 2.28865 1.10952 1.17896 2.47336 1.17896H7.88931C8.12334 1.17896 8.31307 1.36869 8.31307 1.60272V2.73275C8.31307 2.96678 8.12334 3.15651 7.88931 3.15651H2.47336C2.19994 3.15651 1.97755 3.37907 1.97755 3.65259V13.5265C1.97755 13.7999 2.19994 14.0224 2.47336 14.0224H12.3474C12.6209 14.0224 12.8434 13.7999 12.8434 13.5265V8.37432C12.8434 8.14029 13.0332 7.95055 13.2672 7.95055Z"
          fill={productColor.eggplant100}
        />
      </svg>
    ),

    pdf: (
      <svg width="16" height="17" viewBox="0 0 16 17" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.5 7.33203C11.696 7.33203 11.874 7.44603 11.955 7.62503C12.036 7.80403 12.006 8.01403 11.876 8.16103L8.376 12.161C8.281 12.269 8.144 12.332 8 12.332C7.856 12.332 7.719 12.27 7.624 12.161L4.124 8.16103C3.995 8.01303 3.963 7.80403 4.045 7.62503C4.126 7.44703 4.304 7.33203 4.5 7.33203H6.5V0.832031C6.5 0.556031 6.724 0.332031 7 0.332031H9C9.276 0.332031 9.5 0.556031 9.5 0.832031V7.33203H11.5ZM13.5 14.332V11.332H15.5V15.332C15.5 15.885 15.053 16.332 14.5 16.332H1.5C0.948 16.332 0.5 15.885 0.5 15.332V11.332H2.5V14.332H13.5Z"
          fill={productColor.eggplant100}
        />
      </svg>
    ),
  };
  const optionIcon = optionIconDefintions[option];
  const [isHovered, setIsHovered] = React.useState(false);
  return (
    <>
      <a
        style={{
          color: productColor.eggplant100,
          fontWeight: 600,
          textDecoration: isHovered ? 'underline' : 'none',
          display: 'flex',
          alignItems: 'center',
          //justifyContent: 'center',
          gap: '0.5em',
        }}
        href={url}
        target="_blank"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {title}
        {optionIcon}
      </a>
    </>
  );
}

export function InternalSection(props: {|
  title: string,
  children: ChildrenArray<React$Element<any>>,
  timeEstimate?: number,
|}) {
  const { title, children } = props;
  return (
    <div style={{ color: productColor.shuttleGrey, marginTop: 32 }}>
      <InternalHeading level={3}>{title}</InternalHeading>
      {children}
    </div>
  );
}

export function InternalDescription(props: {|
  children: ChildrenArray<MathString>,
|}) {
  return <div style={{ marginTop: 12 }}>{props.children}</div>;
}

export function InternalPanel(props: {
  openByDefault?: boolean,
  heading: string,
  children: ChildrenArray<React$Element<any>>,
}) {
  const inPrintMode = usePrintMode();
  const [_open, setOpen] = React.useState(props.openByDefault || inPrintMode);
  const open = _open;
  return (
    <div
      style={{
        background: productColor.aliceBlue,
        borderRadius: 8,
        marginTop: 12,
        backgroundColor: 'auto',
      }}
    >
      <div style={{ breakInside: 'avoid' }}>
        <div
          style={{
            padding: 16,
            display: 'table',
            cursor: 'pointer',
          }}
          onClick={() => setOpen((prevState) => !prevState)}
        >
          <div
            style={{
              display: 'table-cell',
              verticalAlign: 'middle',
              width: '100%',
            }}
          >
            <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
              <h1
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  margin: 0,
                  color: illustrationColor.pickledBluewood,
                }}
              >
                {props.heading}
              </h1>
            </div>
          </div>
          <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
            <HollowCollapseChevron isOpen={open} />
          </div>
        </div>
        <div
          style={{
            // When we are closed we still want children to be in the DOM
            // AND (critically) we want the children to exist in the box tree
            // as well, so that we can perform accurate layout measurements.
            // This combination of styles ensures children exist in the box
            // tree, but do not affect layout, nor are drawn to the screen.
            visibility: open ? 'visible' : 'hidden',
            height: open ? 'auto' : 0,
            lineHeight: '22px',
            fontWeight: 400,
            padding: open ? '0 16px 16px 16px' : 0,
          }}
        >
          {props.children}
        </div>
      </div>
    </div>
  );
}

export const HollowCollapseChevron = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="16"
    height="10"
    viewBox="0 0 16 10"
    fill="none"
    style={{
      transform: isOpen ? `rotate(-180deg)` : undefined,
      transition: 'transform 0.3s ease-in-out',
    }}
  >
    <path
      d="M0.159847 2.36855L7.63143 9.83962C7.73818 9.94648 7.86099 10 7.99992 10C8.13884 10 8.26188 9.94648 8.36869 9.83962L15.8397 2.36855C15.9467 2.26151 16 2.13871 16 1.99972C16 1.8608 15.9465 1.73782 15.8397 1.63101L15.0382 0.829631C14.9314 0.722769 14.8084 0.669366 14.6694 0.669366C14.5305 0.669366 14.4075 0.722769 14.3007 0.829631L7.99992 7.13028L1.69893 0.829237C1.59212 0.722375 1.46909 0.66931 1.33039 0.66931C1.19124 0.66931 1.06826 0.722713 0.961452 0.829237L0.159904 1.63096C0.0530977 1.73776 3.05176e-05 1.8608 3.05176e-05 1.99967C-2.47955e-05 2.1387 0.0530415 2.26168 0.159847 2.36855Z"
      fill={productColorSunflower.grey}
    />
  </svg>
);
